import { useState, useRef, useEffect } from 'react';

/**
 * OptimizedImage component with lazy loading and WEBP support
 * Improves page load performance by:
 * 1. Lazy loading images when they enter viewport
 * 2. Using optimized image parameters for Unsplash
 * 3. Showing placeholder while loading
 * 4. Handling image errors gracefully
 */

const OptimizedImage = ({ 
  src, 
  alt, 
  className = '', 
  width = 800, 
  quality = 75,
  placeholder = 'blur',
  priority = false,
  showPlaceholderOnError = true,
  onError,
  ...props 
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(priority); // Load immediately if priority
  const [hasError, setHasError] = useState(false);
  const imgRef = useRef(null);

  // Optimize image URL
  const getOptimizedSrc = (originalSrc) => {
    if (!originalSrc) return null;
    
    // For Unsplash images - add optimization params for WEBP
    if (originalSrc.includes('unsplash.com')) {
      const baseUrl = originalSrc.split('?')[0];
      return `${baseUrl}?w=${width}&q=${quality}&fm=webp&auto=format`;
    }
    
    // For other images, return as-is
    return originalSrc;
  };

  // Intersection Observer for lazy loading
  useEffect(() => {
    if (priority) return; // Skip observer if priority loading
    
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      { 
        rootMargin: '50px', // Reduced from 100px for faster trigger
        threshold: 0.01 
      }
    );

    if (imgRef.current) {
      observer.observe(imgRef.current);
    }

    return () => observer.disconnect();
  }, [priority]);

  const optimizedSrc = getOptimizedSrc(src);
  
  // If no source or error, show placeholder
  if (!optimizedSrc || hasError) {
    if (showPlaceholderOnError) {
      return (
        <div 
          ref={imgRef}
          className={`relative overflow-hidden flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200 ${className}`}
        >
          <div className="text-center p-4">
            <div className="w-12 h-12 mx-auto mb-2 rounded-full bg-malva/15 flex items-center justify-center">
              <span className="text-xl text-malva-dark font-serif">{alt?.charAt(0) || '?'}</span>
            </div>
            <p className="text-xs text-gray-400">Image coming soon</p>
          </div>
        </div>
      );
    }
    return null;
  }

  const handleError = (e) => {
    setHasError(true);
    if (onError) onError(e);
  };

  return (
    <div 
      ref={imgRef} 
      className={`relative overflow-hidden ${className}`}
      style={{ backgroundColor: '#f3f4f6' }}
    >
      {/* Placeholder blur effect */}
      {!isLoaded && placeholder === 'blur' && (
        <div 
          className="absolute inset-0 bg-gradient-to-br from-gray-200 to-gray-300 animate-pulse"
        />
      )}
      
      {/* Actual image - only load when in view */}
      {isInView && (
        <img
          src={optimizedSrc}
          alt={alt}
          loading={priority ? "eager" : "lazy"}
          decoding="async"
          onLoad={() => setIsLoaded(true)}
          onError={handleError}
          className={`w-full h-full object-cover transition-opacity duration-300 ${
            isLoaded ? 'opacity-100' : 'opacity-0'
          }`}
          {...props}
        />
      )}
    </div>
  );
};

export default OptimizedImage;
